import { Day, FrequencyType } from './calendar.types'
import { TaxInfo } from './mutual-fund-detail.types'
import { UNITS } from './misc.types'

export type ActionButton = {
  testLocator: string
  text?: string
  icon?: React.ReactNode
  onClick: () => void
  isPrimary: boolean
  isActive?: boolean
  isDisabled: boolean
  width?: number
}

export type ExecutionBarConfig = {
  buttons: ActionButton[]
  learnMore?: {
    text: string
    onClick: () => void
  }
}

export enum ExecutionLearnMoreReason {
  INVESTMENT_PAUSED = 'INVESTMENT_PAUSED',
  WITHDRAWAL_PAUSED = 'WITHDRAWAL_PAUSED',
  NEW_ORDERS_PAUSED = 'NEW_ORDERS_PAUSED',
  SIP_PAUSED = 'SIP_PAUSED',
  ONE_TIME_PAUSED = 'ONE_TIME_PAUSED',
  REGULAR = 'REGULAR',
  GENERIC = 'GENERIC'
}

export enum RegularPlanSheetVariant {
  INVEST = 'invest',
  WITHDRAW = 'withdraw'
}

export enum SubActionDisabledReason {
  PAUSED = 'PAUSED',
  NOT_OFFERED = 'NOT_OFFERED',
  REGULAR = 'REGULAR'
}

export enum WithdrawalLearnMoreReason {
  LOCKIN = 'LOCKIN',
  PLEDGED = 'PLEDGED',
  DEMAT = 'DEMAT',
  CAN_NOT_WITHDRAW = 'CAN_NOT_WITHDRAW',
  GENERIC = 'GENERIC'
}

export enum TransferLearnMoreReason {
  LOCKIN = 'LOCKIN',
  PLEDGED = 'PLEDGED',
  DEMAT = 'DEMAT',
  CAN_NOT_TRANSFER = 'CAN_NOT_TRANSFER',
  GENERIC = 'GENERIC'
}

export type SubActionButton = {
  testLocator?: string
  text?: string
  icon?: string
  onClick: (e?: React.MouseEvent) => void
  isDisabled: boolean
  disabledReason?: SubActionDisabledReason
}

export type SIPFrequencyData = {
  minAmount: number
  maxAmount: number
  stepper?: number
  dayDetails: Day[]
  frequency: 'MONTHLY' | 'WEEKLY' | 'DAILY'
}

export enum FolioSource {
  EXTERNAL = 'EXTERNAL',
  POWER_UP = 'POWER_UP',
  INFINITE = 'INFINITE'
}

export type FolioDetail = {
  folioNumber: string
  folioName: string
  logoUrl: string
  folioAmount: number
  currentValue: number
  saveTax: number
  gains: number
  source: FolioSource
  isUnavailable: boolean
  minAmount: number
  maxAmount: number
  stepper: number
  exitLoad: number
  demat: boolean
  unavailabilityReasons: Array<WithdrawalLearnMoreReason>
}

export enum UserInvestmentStatus {
  INVESTED = 'INVESTED',
  NOT_INVESTED = 'NOT_INVESTED'
}

export enum OnboardingStatus {
  ONBOARDED = 'ONBOARDED',
  NOT_ONBOARDED = 'NOT_ONBOARDED'
}

export type ProcessingPartnerMaintenance = {
  title: string
  description: string
  resumeTimeLabel?: string
  notifyCta?: Record<string, unknown>
}

export type MFExecutionData = {
  userInvestmentStatus: UserInvestmentStatus
  executionOnboardingStatus: OnboardingStatus
  isWhitelisted: boolean
  isFamilyAccess: boolean
  isInfiniteUser: boolean
  cta: Record<any, any>
  buy: {
    minAmount: number
    maxAmount: number
    stepper?: number
  }
  sip: SIPFrequencyData[]
  sell: {
    totalAvailableAmount: number
    folioDetails: FolioDetail[]
  }
  swp: null
  transfer: null
  processingPartnerMaintenance?: ProcessingPartnerMaintenance | null
  buttonDisabledStates: {
    learnMoreReason?: ExecutionLearnMoreReason
    investButton: {
      isDisabled: boolean
      disabledReason?: SubActionDisabledReason
    }
    // withdrawButton is deprecated except for isDisabled key
    withdrawButton: {
      isDisabled: boolean
      disabledReason?: WithdrawalLearnMoreReason
    }
    moreOptionsButton: {
      isDisabled: boolean
    }
    buy: {
      isDisabled: boolean
      disabledReason?: SubActionDisabledReason
    }
    sip: {
      isDisabled: boolean
      disabledReason?: SubActionDisabledReason
    }
    sell: {
      isDisabled: boolean
      disabledReason?: SubActionDisabledReason
    }
    swp: {
      isDisabled: boolean
      disabledReason?: SubActionDisabledReason
    }
    transfer: {
      isDisabled: boolean
      disabledReason?: TransferLearnMoreReason
    }
  }
}

export enum PaymentPageType {
  BUY = 'buy',
  SIP = 'sip',
  WITHDRAW = 'withdraw',
  SWP = 'swp',
  TRANSFER = 'transfer'
}

export enum InvestPaymentMethodTypes {
  MANDATE = 'MANDATE',
  NET_BANKING = 'NET_BANKING',
  UPI = 'UPI',
  UPI_INTENT = 'UPI_INTENT'
}

export type InvestPaymentMethodType = {
  paymentMethodId?: string
  type: `${InvestPaymentMethodTypes}`
  displayAccNumber: string
  maximumAllowedAmount?: number
  upiId?: string | null
  status?: string | null
  accountNumber?: string | null
  ifscCode?: string | null
  logoUrl?: string
  upiPackageName?: string | null
}

export type PaymentDetailsResponse = {
  paymentMethods: InvestPaymentMethodType[]
  defaultPaymentMethodDetails: InvestPaymentMethodType | null
  bankAccountDetails: InvestPaymentMethodType[]
  upiIntentMaximumAllowedAmount: number
}

export type BuyMasterPayload = {
  paymentMethodId: string
  paymentMethodType: `${InvestPaymentMethodTypes}`
  transactionType: 'BUY'
  isin: string
  amount: number
  upiPackageName?: string | null
}

export type SIPMasterPayload = {
  paymentMethodId: string
  paymentMethodType: `${InvestPaymentMethodTypes}`
  transactionType: 'SIP'
  isin: string
  amount: number
  frequencyValue: number
  frequencyType: FrequencyType
  upiPackageName?: string | null
}

export type WithdrawAccountDetail = {
  accountId: string
  displayAccNumber: string
  logoUrl: string
}

export type FetchUserBankAccountsResponse = {
  folioAccountDetailsList: WithdrawAccountDetail[]
}

export type WithdrawMasterPayload = {
  accountId: WithdrawAccountDetail['accountId']
  transactionType: 'REDEMPTION'
  isAllUnits: boolean
  isin: string
  amount: number
  folioNumber: FolioDetail['folioNumber']
}

export type SWPMasterPayload = {
  swp: {
    amount: number
    frequency: FrequencyType
    startDate: string
  } | null
}

export type TransferMasterPayload = {
  transfer: {
    units: number
    targetFundId: string
    targetFundName: string
  } | null
}

export type OrderOTPTriggerPayload = {
  paymentMethodId: string | null
  externalSell?: boolean
  paymentMethodType?: `${InvestPaymentMethodTypes}`
  transactionType: 'BUY' | 'REDEMPTION'
  isin: string
  amount: number
  folioNumber?: FolioDetail['folioNumber']
  isAllUnits?: boolean
  upiPackageName?: string | null
}

export type OrderOTPTriggerResponse = {
  otpReferenceId: string
}

export type OrderPayload = {
  otp: string
  otpReferenceId: string
  orderDetails: OrderOTPTriggerPayload
}

export enum OrderOtpState {
  MAXIMUM_LIMIT_REACHED = 'MAXIMUM_LIMIT_REACHED',
  INVALID_OTP = 'INVALID_OTP',
  MAXIMUM_INCORRECT_RESPONSE = 'MAXIMUM_INCORRECT_RESPONSE',
  SUCCESS = 'SUCCESS',
  UNKNOWN = 'UNKNOWN'
}

export type OrderResponse = {
  orderId: string
  isOtpVerified: boolean
  otpState: OrderOtpState
}

export type SystematicOTPTriggerPayload = {
  paymentMethodId: string
  paymentMethodType: `${InvestPaymentMethodTypes}`
  transactionType: 'SIP' | 'SWP'
  isin: string
  amount: number
  frequencyType?: FrequencyType
  frequencyValue?: number
}

export type SystematicOTPTriggerResponse = {
  otpReferenceId: string
}

export type SystematicPayload = {
  otp: string
  otpReferenceId: string
  sxpDetails: SystematicOTPTriggerPayload
}

export type SystematicResponse = {
  orderId: string
  sxpId: string
  isOtpVerified: boolean
  otpState: OrderOtpState
}

export type PaymentStatusResponse = {
  type: 'UPI' | 'NET_BANKING' | 'UPI_INTENT'
  status: `${PaymentStatus}`
  actionUrl?: string
  formMethod?: string
  parameters?: Record<string, string>
  upiPackageName?: string | null
}

export enum PaymentStatus {
  AWAITING = 'AWAITING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED'
}

export enum TerminalStatus {
  LOADING = 'LOADING',
  AWAITING = 'AWAITING',
  PENDING = 'PENDING',
  FULL_SUCCESS = 'FULL_SUCCESS',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
  FAILURE = 'FAILURE'
}

export enum TerminalStatusContext {
  AUTO_PAY = 'AUTO_PAY',
  SIP = 'SIP',
  BUY = 'BUY',
  WITHDRAW = 'WITHDRAW',
  TRANSFER = 'TRANSFER'
}

export enum AutoPayStatus {
  ENABLED = 'ENABLED',
  SETUP_PENDING = 'SETUP_PENDING',
  PENDING = 'PENDING',
  FALLS_SHORT = 'FALLS_SHORT',
  FAILED = 'FAILED'
}

export enum AutoPayMandateType {
  UPI_AUTOPAY = 'UPI_AUTOPAY',
  NET_BANKING_AUTOPAY = 'NET_BANKING_AUTOPAY'
}

/** V1 autopay bank list (GET .../mandates). Legacy flow only. */
export type AutoPayMethodResponse = {
  mandateId: string
  displayAccNumber: string
  bankUrlLogo: string
  autoPayStatus: AutoPayStatus
  accountId: string
}[]

/** Bank account row in mandate-details (v2) — used for net banking setup UI. */
export type AutoPayBankAccount = {
  accountId: string
  displayAccNumber: string
  bankUrlLogo: string
}

/** Single bank/mandate row inside mandate-details (v2). */
export type AutoPayMandateDetails = {
  accountId: string
  mandateId: string | null
  displayAccNumber: string
  bankUrlLogo: string
  autoPayStatus: AutoPayStatus
  mandateType: AutoPayMandateType | null
}

/** V2 mandate-details API response (GET .../systematic/:sxpId/mandate-details). */
export type MandateDetailsResponse = {
  sipDate: number
  maxLimit: number
  /** SIP-level required autopay type — default for UI; user can still pick the other mode when allowed. */
  mandateType: AutoPayMandateType
  /** Per-account existing mandates (each row is typed, e.g. UPI_AUTOPAY vs NET_BANKING). */
  availableMandates: AutoPayMandateDetails[]
  /** Banks eligible for setup; net-banking list and UPI linked-bank picker read from here. */
  bankAccounts: AutoPayBankAccount[] | null
}

export type AutopayPaymentMode = 'UPI' | 'NET_BANKING'

/** User's chosen autopay mode + bank/UPI app on SetupAutoPay / AutopayMethodsScreen. */
export type AutopayMethodSelection = {
  mode: AutopayPaymentMode
  accountId: string
  mandateId: string | null
  displayAccNumber: string
  bankUrlLogo: string
  upiPackageName?: string | null
  upiAppName?: string
  upiAppLogoUrl?: string
}

export type SetupAutoPayPayload = {
  sxpId: string
  mandateId: string
  accountId: string
  /** POST .../mandate — `UPI_AUTOPAY` when a UPI app is chosen, else `NET_BANKING`. */
  mandateType: SetupAutoPayMandateType
}

export type SetupAutoPayMandateType = 'UPI_AUTOPAY' | 'NET_BANKING'

export enum AutoPaySetupStatus {
  INITIATED = 'INITIATED',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED'
}

export type SetupAutoPayResponse = {
  sxpId: string
  mandateId: string
  status: AutoPaySetupStatus
}

export type AutoPayStatusPayload = {
  sxpId: string
  mandateId: string
  /** Set when autopay setup used a UPI app; forwarded to GET .../sxp-mandate/status. */
  upiPackageName?: string | null
}

export type AutoPayStatusResponse = {
  sxpId: string
  mandateId: string
  status: AutoPaySetupStatus
  /** Redirect / intent URL — net-banking (`AutoPayLinkWaitingPage`) or UPI intent (`AutoPayUpiLinkWaitingPage`). */
  link?: string
  upiPackageName?: string | null
}

export type TaxCalculationsPayload = {
  folioNumber: FolioDetail['folioNumber']
  isin: string
  amount: number
}

export type TaxCalculationsResponse = {
  exitLoadSlabs: ExitLoad[]
  stcgTaxInfo: TaxInfo
  ltcgTaxInfo: TaxInfo
  grandfatheringRuleApplicable: boolean
}

export type Range = {
  min: number
  max: number
  unit: `${UNITS}`
}

export type ExitLoad = {
  amountRange: Range
  timeRange: Range
  rate: number
}

export type UPIAdditionBankAccount = {
  logoUrl: string
  displayAccNumber: string
  paymentMethodId: string
  accountNumber: string
  ifscCode: string
}

export type UPISetupPayload = {
  accountNumber: string
  ifscCode: string
  upiId: string
}

export type UPISetupResponse = {
  paymentMethodId: string
  status: 'INVALID' | 'VALID'
  maximumAllowedAmount: number
}
